/**
 * Couleur du texte posé sur un fond plein d'action (SAAS-1185).
 *
 * @remarks
 * Les boutons pleins (`bg-primary-strong`, `bg-error-strong`) prennent la
 * couleur d'accent mélangée à du noir en OKLab (`color-mix(in oklab, c p%,
 * black)` dans `tokens.css`). L'accent est personnalisable : sur un accent
 * clair (vert, ambre), même assombri, le blanc reste illisible. On refait ici
 * le même mélange que le CSS pour choisir le texte : blanc s'il atteint 4,5:1,
 * sinon le plus lisible du blanc et du quasi-noir.
 *
 * Mélanger avec du noir en OKLab revient à multiplier L, a et b par `p`.
 */

const WHITE = '#ffffff';
const INK = '#0a0a0f';
/** Seuil WCAG AA du texte courant. */
const AA = 4.5;

type Rgb = readonly [number, number, number];

/** `noUncheckedIndexedAccess` : un tableau de 3 nombres redevient un triplet. */
const rgb = (v: readonly number[]): Rgb => [v[0] ?? 0, v[1] ?? 0, v[2] ?? 0];

function parseHex(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;
  const digits = match[1].length === 3 ? match[1].replace(/./g, '$&$&') : match[1];
  return rgb([0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16) / 255));
}

const toLinear = (v: number): number => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const toGamma = (v: number): number =>
  v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
const clamp = (v: number): number => Math.min(1, Math.max(0, v));

/** sRGB (gamma) → sRGB (gamma), assombri en OKLab comme `color-mix(..., black)`. */
function darkenOklab(color: Rgb, p: number): Rgb {
  const [lr, lg, lb] = rgb(color.map(toLinear));
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L = (0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s) * p;
  const A = (1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s) * p;
  const B = (0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s) * p;
  const l3 = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m3 = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s3 = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return rgb(
    [
      4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
      -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
      -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
    ].map((v) => clamp(toGamma(v))),
  );
}

function luminance(color: Rgb): number {
  const [r, g, b] = rgb(color.map(toLinear));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Rapport de contraste WCAG entre deux couleurs. */
function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Texte à poser sur `accent` mélangé à `(1 - p)` de noir. Une valeur non
 * hexadécimale (variable CSS, `rgb()`) garde le blanc par défaut.
 *
 * @param accent couleur hexadécimale choisie par l'utilisateur
 * @param p part d'accent dans le mélange (0.88 = 12 % de noir)
 */
export function textOnStrong(accent: string, p: number): string {
  const color = parseHex(accent);
  if (color === null) return WHITE;
  const bg = darkenOklab(color, p);
  const white = contrastRatio([1, 1, 1], bg);
  if (white >= AA) return WHITE;
  return white >= contrastRatio(parseHex(INK) ?? [0, 0, 0], bg) ? WHITE : INK;
}
